import { FormikContextType } from "formik";
import classNames from "classnames";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { CountInput } from "@design-system/CountInput/CountInput";
import { pluralize } from "@common/helpers/string";
import { RecurrenceLeadUnit } from "@common/types/serviceTemplateRecurrence";
import recurrenceStyles from "@components/accounting/journalEntryDrawer/JournalEntryRecurrenceSelect.module.scss";
import type { ApplyCloseTemplateWizardValues } from "./applyCloseTemplate.schema";
import styles from "./ScheduleTemplateDrawer.module.scss";

interface Props {
    formik: FormikContextType<ApplyCloseTemplateWizardValues>;
}

const LEAD_UNIT_LABELS: Record<RecurrenceLeadUnit, string> = {
    [RecurrenceLeadUnit.DAYS]: "Days",
    [RecurrenceLeadUnit.WEEKS]: "Weeks",
    [RecurrenceLeadUnit.MONTHS]: "Months",
};

const LEAD_UNIT_ITEMS: Array<PickerItemData<RecurrenceLeadUnit>> =
    Object.values(RecurrenceLeadUnit).map((unit) => ({
        value: unit,
        label: LEAD_UNIT_LABELS[unit],
    }));

type EndsMode = "never" | "on" | "after";

function getEndsMode(schedule: {
    endDate: string | null;
    maxOccurrenceCount: number | null;
}): EndsMode {
    if (schedule.endDate != null) {
        return "on";
    }
    if (schedule.maxOccurrenceCount != null) {
        return "after";
    }
    return "never";
}

export const ScheduleTemplateStepSchedule = ({ formik }: Props) => {
    const schedule = formik.values.defaults.schedule;
    if (!schedule) {
        return null;
    }

    const endsMode = getEndsMode(schedule);
    // startDate is already a validated ISO date string (or empty when cleared),
    // so use it directly — round-tripping through parseISO/formatISO throws on
    // an empty value and white-screens the wizard.
    const defaultEndDate = schedule.endDate ?? schedule.startDate;

    const setEndsMode = (mode: EndsMode) => {
        if (mode === "never") {
            void formik.setFieldValue("defaults.schedule.endDate", null);
            void formik.setFieldValue(
                "defaults.schedule.maxOccurrenceCount",
                null,
            );
            return;
        }
        if (mode === "on") {
            void formik.setFieldValue(
                "defaults.schedule.endDate",
                defaultEndDate,
            );
            void formik.setFieldValue(
                "defaults.schedule.maxOccurrenceCount",
                null,
            );
            return;
        }
        void formik.setFieldValue("defaults.schedule.endDate", null);
        void formik.setFieldValue(
            "defaults.schedule.maxOccurrenceCount",
            schedule.maxOccurrenceCount ?? 1,
        );
    };

    return (
        <VStack gap={16}>
            <div>
                <label className={styles.fieldLabel}>
                    Application lead time
                </label>
                <HStack gap={8}>
                    <Field.Root className={styles.leadTimeValueInput}>
                        <Input
                            type="number"
                            size="xl"
                            value={schedule.applicationLeadValue}
                            placeholder="0"
                            onChange={(event) => {
                                void formik.setFieldValue(
                                    "defaults.schedule.applicationLeadValue",
                                    parseInt(event.target.value, 10) || 0,
                                );
                            }}
                        />
                    </Field.Root>
                    <Picker.Select
                        items={LEAD_UNIT_ITEMS}
                        value={schedule.applicationLeadUnit}
                        onValueChange={(value) => {
                            if (value) {
                                void formik.setFieldValue(
                                    "defaults.schedule.applicationLeadUnit",
                                    value,
                                );
                            }
                        }}
                        trigger={<Picker.Button variant="primary" size="xl" />}
                    />
                </HStack>
            </div>

            <VStack gap={8}>
                <h4 className={styles.endsHeading}>Ends</h4>
                <RadioGroup<EndsMode>
                    name="st-endsMode"
                    aria-label="Ends"
                    value={endsMode}
                    onValueChange={setEndsMode}
                    className={classNames(
                        recurrenceStyles.block,
                        styles.recurrenceBlock,
                    )}
                >
                    <div className={recurrenceStyles.line}>
                        <div className={recurrenceStyles.leftLabel}>
                            <Radio value="never" label="Never" />
                        </div>
                        <div className={recurrenceStyles.fieldContainer}>
                            <span className={recurrenceStyles.neverDescription}>
                                Applications due today or earlier are applied
                                immediately.
                            </span>
                        </div>
                    </div>

                    <div className={recurrenceStyles.line}>
                        <div className={recurrenceStyles.leftLabel}>
                            <Radio value="on" label="On" />
                        </div>
                        <div className={recurrenceStyles.endDateContainer}>
                            <DatePicker
                                mode="day"
                                variant="button"
                                disabled={endsMode !== "on"}
                                buttonProps={{ size: "xl" }}
                                clearable={false}
                                value={
                                    (schedule.endDate ?? defaultEndDate) ||
                                    undefined
                                }
                                onValueChange={(value) => {
                                    setEndsMode("on");
                                    void formik.setFieldValue(
                                        "defaults.schedule.endDate",
                                        value ?? null,
                                    );
                                }}
                            />
                        </div>
                    </div>

                    <div className={recurrenceStyles.line}>
                        <div className={recurrenceStyles.leftLabel}>
                            <Radio value="after" label="After" />
                        </div>
                        <div className={recurrenceStyles.fieldContainer}>
                            <CountInput
                                disabled={endsMode !== "after"}
                                min={1}
                                value={schedule.maxOccurrenceCount ?? 1}
                                onValueChange={(value) => {
                                    setEndsMode("after");
                                    void formik.setFieldValue(
                                        "defaults.schedule.maxOccurrenceCount",
                                        Math.max(value, 1),
                                    );
                                }}
                                className={recurrenceStyles.countField}
                            />
                            <span
                                className={classNames(
                                    recurrenceStyles.occurrenceSuffix,
                                    {
                                        [recurrenceStyles.occurrenceDisabled]:
                                            endsMode !== "after",
                                    },
                                )}
                            >
                                {pluralize(
                                    "occurrence",
                                    schedule.maxOccurrenceCount ?? 1,
                                )}
                            </span>
                        </div>
                    </div>
                </RadioGroup>
            </VStack>
        </VStack>
    );
};
